import { Injectable, ViewContainerRef, forwardRef, Inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, flatMap, map, tap } from 'rxjs/operators';
import * as urltemplate from 'url-template';

import { LMActionService, LMActionRequest } from '@services/lm-action.service';
import { AppConstants } from '@utils/app.constants';
import { LMTransactionService, LMTransactionState } from '@transaction/index';

@Injectable()
export class LMListTransactionService {
  constructor(
    private lmActionService: LMActionService,
    private lmTransactionService: LMTransactionService
  ) {}

  private createTransactionUrl(
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    dataView: LMDataView
  ): string {
    const listSpec = listContext.listSpec;
    let resourceId = dataView.resourceId;

    if (listSpec.isTemplateList) {
      resourceId = listSpec.templateTargetResourceId.split('.')[0];
    }

    const transactionUrlTemplate = urltemplate.parse(listContext.listUrlInfo.transactionUrl);
    const actionName = actionSpec.name;
    return transactionUrlTemplate.expand({ resourceId, actionName });
  }

  private createActionRedirectUrl(
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    dataViewList: LMDataView[],
    contextResourceId?: string
  ): string {
    const listSpec = listContext.listSpec;
    const dataView = dataViewList[ 0 ];     // is this correct? should it be the header on dataViewSet

    contextResourceId = contextResourceId || dataViewSet.header.resourceId;
    if (listSpec.isTemplateList) {
      contextResourceId = listSpec.templateTargetResourceId.split('.')[0];
    }

    let resourceId: string;
    if (AppConstants.ACTION_TYPE_CREATE === actionSpec.type) {
      resourceId = dataViewSet.header.resourceId;
    } else {
      resourceId = dataView.resourceId;
    }

    const actionRedirectUrlTemplate = urltemplate.parse(listContext.listUrlInfo.actionRedirectUrl);
    const actionName = actionSpec.name;
    const url = actionRedirectUrlTemplate.expand({ resourceId, actionName, contextResourceId });
    return url;
  }

  public runSetAction(listContext: LMListContext, actionSpec: LMActionSpec, dataViewSet: LMDataViewSet): Observable<LMTransactionState> {
    const url = this.createTransactionUrl(listContext, actionSpec, dataViewSet, dataViewSet.header);
    const transaction = new LMTransactionState(url);
    const dataViewList = [ dataViewSet.header ];

    const actionChecks$ = this.runActionChecks(transaction, listContext, actionSpec, dataViewSet, dataViewList);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runInstanceAction(
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    dataViewList: LMDataView[],
    contextDataView?: LMDataView,
    actionFieldMap?: LMActionFieldMap,
    dragdropTargetView?: LMDataView
  ): Observable<LMTransactionState> {
    const listSpec = listContext.listSpec;
    const url = this.createTransactionUrl(listContext, actionSpec, dataViewSet, dataViewSet.header);
    const transaction = new LMTransactionState(url);

    if (listSpec.isTemplateList) {
      dataViewList = dataViewList.map(dataView => dataView.dataView);
    }

    const actionChecks$ = this.runActionChecks(
      transaction,
      listContext,
      actionSpec,
      dataViewSet,
      dataViewList,
      contextDataView,
      actionFieldMap,
      dragdropTargetView
    );
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  private runActionChecks(
    transactionState: LMTransactionState,
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    dataViewLists: LMDataView[],
    contextDataView?: LMDataView,
    actionFieldMap?: LMActionFieldMap,
    dragdropTargetView?: LMDataView
  ): Observable<boolean> {
    const listSpec = listContext.listSpec;
    const buildTransactionRequests = (scheduleView: LMDataView) =>
      this.lmActionService.buildActionRequestForDataViewList({
        dataViewLists,
        actionSpec,
        scheduleView
      });

    // get schedule view details
    return this.lmTransactionService.getScheduleView(actionSpec, listSpec.busClassSpec.name).pipe(
      filter(scheduleView => (scheduleView !== undefined)),
      // build action requests payload
      map((scheduleView: LMDataView) => buildTransactionRequests(scheduleView)),
      // builds action url
      map((transactionRequests: LMActionRequest[]) => {
        transactionState.setRequestPayload(transactionRequests);
        return this.createActionRedirectUrl(listContext, actionSpec, dataViewSet, dataViewLists);
      }),
      // check if form input is required
      flatMap((actionUrl: string) => this.lmTransactionService.runCheckFormInputRequired(
        actionUrl,
        actionSpec,
        transactionState,
        listContext.stepUpActionResult,
        contextDataView,
        actionFieldMap,
        dragdropTargetView
      )),
      // proceed if form input is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transactionState, listSpec.busClassSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transactionState)),
      catchError(this.lmTransactionService.checkCancelled)
    );
  }

  public runCreateAction(
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    contextResourceId: string
  ): Observable<LMTransactionState> {
    const dataViewList = [ dataViewSet.header ];
    const redirectUrl = this.createActionRedirectUrl(listContext, actionSpec, dataViewSet, dataViewList, contextResourceId);
    const transaction = new LMTransactionState(redirectUrl);

    const proceed$ = this.lmTransactionService.runCheckFormInputRequired(
      redirectUrl,
      actionSpec,
      transaction,
      listContext.stepUpActionResult
    ).pipe(
      // proceed if form input is made or it's not required, then checks if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, listContext.listSpec.busClassSpec)),
      // proceed if response is made or it's not required, then checks if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runSaveAction(
    listContext: LMListContext,
    dataViewSet: LMDataViewSet,
    dataViewList: LMDataView[]
  ): Observable<LMTransactionState> {
    const listSpec = listContext.listSpec;
    const cubePeriodKey = (listContext.listSpec.busListSpec.implementsCubeView) ? dataViewSet.cubeInfo.periodKey : undefined;
    const cubePeriodView = (listContext.listSpec.busListSpec.implementsCubeView) ? dataViewSet.cubeInfo.periodView : undefined;
    const newSequenceField = listSpec.busListSpec.newSequenceField;
    const transactionRequests = this.lmActionService.buildActionRequestForSaveList(dataViewList, listSpec, newSequenceField, cubePeriodKey, cubePeriodView);
    const actionSpec = transactionRequests[0].actionSpec;
    const url = this.createTransactionUrl(listContext, actionSpec, dataViewSet, dataViewSet.header);
    const transaction = new LMTransactionState(url);
    transaction.setRequestPayload(transactionRequests);

    const proceed$ = this.lmTransactionService.runCheckResponseRequired(
      transaction,
      listSpec.busClassSpec
    ).pipe(
      // proceed if response is made or it's not required, then checks if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runDeleteAction(
    listContext: LMListContext,
    actionSpec: LMActionSpec,
    dataViewSet: LMDataViewSet,
    dataViewList: LMDataView[]
  ): Observable<LMTransactionState> {
    return this.runInstanceAction(listContext, actionSpec, dataViewSet, dataViewList);
  }

  public runProcessFlowAction(
    listContext: LMListContext,
    pfAction: LMProcessFlowAction,
    dataViewList: LMDataView[],
    dataViewSet: LMDataViewSet
  ): Observable<LMTransactionState> {
    const listSpec = listContext.listSpec;
    const transactionUrlTemplate = urltemplate.parse(listContext.listUrlInfo.transactionUrl);
    let transactionUrl = transactionUrlTemplate.expand({
      resourceId: dataViewSet.header.resourceId,
      actionName: undefined
    });
    transactionUrl = `${transactionUrl}&executionType=_processflow`;

    const transaction = new LMTransactionState(transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: dataViewList, pfAction });
    transaction.setRequestPayload(transactionRequests);

    const busClassSpec = listSpec.busClassSpec;
    const proceed$ = this.lmTransactionService.runCheckResponseRequiredPF(
      transaction,
      pfAction,
      busClassSpec
    ).pipe(
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, busClassSpec)),
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(undefined, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }
}

export interface LMListContext {
  listUrlInfo: LMListUrlInfo,
  listSpec: LMListSpec;
  stepUpActionResult?: LMActionResult;
}
